import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useCallback, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { useResizeObserver } from 'usehooks-ts';
import { InputContainer } from '../input-container/input-container.js';

function _TextArea({ value, label, placeholder, ariaLabel: _ariaLabel, onChange, onClear, error, helperText, disabled, startAdornment, endAdornment, requiredIndicator, minRows = 1, maxRows = 10, ...props }) {
    const [height, setHeight] = useState(null);
    const textAreaRef = useRef(null);
    // This element is used to do the height calculations
    const shadowTextAreaRef = useRef(null);
    const normalizedMinRows = Math.max(1, minRows);
    const normalizedMaxRows = Math.max(1, maxRows);
    const calculateTextAreaHeight = useCallback(() => {
        if (shadowTextAreaRef.current) {
            // Get current text height by inserting the value in the shadow textarea
            shadowTextAreaRef.current.value = value;
            const currentHeight = shadowTextAreaRef.current.scrollHeight;
            // Reset the value of the shadow textarea to get the height of a single line
            // The string cannot be empty, otherwise scrollHeight will be 0
            shadowTextAreaRef.current.value = " ";
            const singleLineHeight = shadowTextAreaRef.current.scrollHeight;
            const minHeight = normalizedMinRows * singleLineHeight;
            const maxHeight = normalizedMaxRows * singleLineHeight;
            const newHeight = Math.min(Math.max(currentHeight, minHeight), maxHeight);
            setHeight(Math.min(newHeight, maxHeight));
        }
    }, [normalizedMaxRows, normalizedMinRows, value]);
    useResizeObserver({
        ref: textAreaRef,
        onResize: calculateTextAreaHeight,
    });
    useLayoutEffect(() => {
        calculateTextAreaHeight();
    }, [value, calculateTextAreaHeight]);
    return (jsx(InputContainer, { label: label, error: error, showClearButton: !!onClear && !!value, onClear: onClear, helperText: helperText, disabled: disabled, startAdornment: startAdornment, endAdornment: endAdornment, requiredIndicator: requiredIndicator, cursor: "text", children: jsxs(Wrapper, { children: [jsx(StyledTextarea, { ref: textAreaRef, placeholder: placeholder, value: value, onChange: (e) => onChange(e.target.value, e), rows: 1, style: {
                        height: height ?? undefined,
                    }, disabled: disabled, ...props }), jsx(VisuallyHiddenTextarea, { ref: shadowTextAreaRef, tabIndex: -1, readOnly: true, "aria-hidden": true })] }) }));
}
const TextArea = forwardRef(_TextArea);
const Wrapper = styled.div `
  position: relative;
`;
const StyledTextarea = styled.textarea `
  display: block;
  font: inherit;
  color: inherit;
  background-color: transparent;
  border: none;
  outline: none;
  padding: 0;
  width: 100%;
  resize: none;
`;
const VisuallyHiddenTextarea = styled(StyledTextarea) `
  position: absolute;
  left: 0;
  bottom: 0;
  height: 0;
  width: 100%;
  overflow: hidden;
  visibility: hidden;
  padding: 0;
`;

export { TextArea };
